// Client tipado de `retail-customer-business` (SureKeep F2 · componente 03 · pista Retail).
// Backend probado E2E en dev (12/12 endpoints, 2026-07-20). El OpenAPI de este lambda declara las
// respuestas como `ApiResponse` genérico, así que tipamos localmente la forma que consume la UI
// (hand-rolled, mismo criterio que `retail-wizard/contract.ts`).
//
// Endpoints públicos (EDGE, protegidos W4 + `@RequirePermission`, scope TENANT). La cookie `session`
// la reenvía el proxy de Vite (vite.config.ts, regla `/api/retail-customer` → api-dev.tufiado.com).
import * as Sentry from '@sentry/react';
import { apiFetch } from '../../client';
import { UnexpectedShapeError } from '../../errors';

const BASE_URL = import.meta.env.VITE_API_RETAIL_CUSTOMER_BASE_URL;

/** Estado de la apertura async de la cuenta PagoConFiado (`AccountStatusEnum` del type-kit). */
export type AccountStatus = 'OPENING' | 'OPEN' | 'FAILED' | 'LINKED';

/** Estado del KYC retail (`RetailKycStatusEnum` del type-kit). */
export type RetailKycStatus = 'PENDING' | 'VERIFIED' | 'FAILED' | 'MANUAL' | 'REVIEW';

/** Estado de vigencia del cliente (`RetailCustomerStatusEnum`): el soft-delete del silo. */
export type RetailCustomerStatus = 'ACTIVE' | 'INACTIVE';

/**
 * Domicilio embebido en el cliente (`RetailAddress`), el shape ligero que sale del OCR del INE o de
 * la captura manual. TODOS los campos son opcionales: una credencial puede no traer número ni CP.
 */
export interface RetailAddress {
    fullAddress?: string;
    street?: string;
    externalNumber?: string;
    internalNumber?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    reference?: string;
}

/**
 * Subconjunto de `CustomerResponse` (type-kit retailCustomer) que consume el portal.
 *
 * El nombre viaja PARTIDO en tres campos, que es como el silo lo persiste y lo devuelve: no existe
 * un `name` compuesto en la ficha. Para pintarlo en una celda está `customerFullName`.
 */
export interface RetailCustomerSummary {
    retailCustomerId: string;
    /**
     * NO es el negocio: el silo escribe aquí el id del TENANT. Pedir la tienda o las verificaciones
     * con este valor devuelve 404 y lista vacía — el negocio del cliente sale del padrón.
     */
    retailerId?: string | null;
    names: string;
    paternalLastName: string;
    maternalLastName?: string | null;
    phone: string;
    phoneVerified?: boolean | null;
    originStoreId?: string | null;
    curp?: string | null;
    rfc?: string | null;
    email?: string | null;
    birthDate?: string | null;
    documentType?: string | null;
    documentExpiresAt?: string | null;
    pcfAccountLevel?: 1 | 2 | null;
    accountStatus?: AccountStatus | null;
    /** Por qué falló la apertura de la cuenta. Solo tiene texto con `accountStatus: FAILED`. */
    failReason?: string | null;
    welcomeAccredited?: boolean | null;
    kycStatus?: RetailKycStatus | null;
    kycModality?: string | null;
    kycScore?: number | null;
    address?: RetailAddress | null;
    addressConfirmed?: boolean | null;
    pldFlag?: boolean | null;
    notes?: string | null;
    status?: RetailCustomerStatus | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    /** La verificación de MetaMap que dejó el veredicto de `kycStatus`. */
    metamapVerificationId?: string | null;
    /** Los dos ids con los que el silo cruza el puente a la pista Fiado (cuenta, tarjeta, KYC). */
    fiadoDirectoryId?: string | null;
    fiadoPeopleId?: string | null;
}

/** Nombre completo en una cadena. Misma composición que hace el silo para el listado. */
export function customerFullName(
    customer: Pick<RetailCustomerSummary, 'names' | 'paternalLastName' | 'maternalLastName'>,
): string {
    return [customer.names, customer.paternalLastName, customer.maternalLastName]
        .filter(Boolean)
        .join(' ');
}

/** Request de `publicUpdateCustomer` (PII editable por el BO), calcado de `UpdateCustomerRequest`. */
export interface UpdateCustomerInput {
    names?: string;
    paternalLastName?: string;
    maternalLastName?: string;
    phone?: string;
    curp?: string;
    rfc?: string;
    email?: string;
    notes?: string;
    birthDate?: string;
}

/**
 * Renglón del listado (`CustomerListItem` del silo). Vista REDUCIDA a propósito: CURP, domicilio
 * y fecha de nacimiento no viajan en la lista — para eso se abre la ficha.
 */
export interface CustomerListItem {
    retailCustomerId: string;
    /** Ya compuesto por el silo: la tabla pinta una sola celda «Cliente». */
    fullName: string;
    phone: string;
    /** `null` hasta que la cuenta PagoConFiado abre. */
    pcfAccountLevel: 1 | 2 | null;
    accountStatus: AccountStatus | null;
    kycStatus: RetailKycStatus;
    createdAt: string;
}

/** Página del listado. Ver `listCustomers` para por qué `nextToken` manda sobre `items`. */
export interface CustomerListPage {
    items: CustomerListItem[];
    count: number;
    nextToken?: string | null;
}

/** Filtros que el silo aplica en DynamoDB. El resto de la pantalla filtra en memoria. */
export interface CustomerListFilters {
    /** Tamaño de página, 1–100. El silo usa 25 si no viaja. */
    limit?: number;
    nextToken?: string;
    /** Nivel de la cuenta PagoConFiado. */
    level?: 1 | 2;
    /** `true` = solo los que pasaron el KYC (VERIFIED); `false` = solo los que no. */
    kyc?: boolean;
}

export type KycReviewResult = 'APPROVED' | 'REJECTED';

/**
 * Cuenta PagoConFiado del cliente, resuelta al vuelo contra el silo de cuentas (`Customer360Wallet`
 * del type-kit). La CLABE nunca se guarda en SureKeep: aquí tampoco se persiste ni se loguea.
 */
export interface Customer360Wallet {
    accountLevel: 1 | 2 | null;
    clabe: string | null;
    /** Saldo en centavos, o `null` cuando la fuente no lo trajo. */
    balanceCents: number | null;
}

/** Compra del cliente en el historial del 360 (`Customer360Purchase` del type-kit). */
export interface Customer360Purchase {
    saleId: string;
    sku: string;
    amountCents: number;
    folio: string;
    soldAt: string;
    status: string;
}

/** Prospecto vinculado al cliente en el 360 (`Customer360Prospect` del type-kit). */
export interface Customer360Prospect {
    prospectId: string;
    pendingReason: string;
    status: string;
    nextContactAt: string;
}

/** Estado de la tarjeta del cliente (`Card.Status` del type-kit). */
export type CardStatus = 'ACTIVE' | 'PENDING' | 'PAUSED' | 'CANCELLED' | 'PIN_BLOCKED';

/** Si la tarjeta es de plástico o solo vive en la app (`Card.VirtualOrPhysical` del type-kit). */
export type CardMedium = 'VIRTUAL' | 'PHYSICAL';

/**
 * Tarjeta del cliente en el 360 (`Customer360Card` del type-kit), compuesta desde el silo de
 * tarjetas. Con `issued: false` los otros tres van en `null`: no hay tarjeta que describir.
 */
export interface Customer360Card {
    issued: boolean;
    /** Últimos dígitos del PAN, tal como los nombra `CardResponse.panLastDigit`. */
    panLastDigit: string | null;
    status: CardStatus | null;
    virtualOrPhysical: CardMedium | null;
    /** Cuándo se emitió la tarjeta. El silo puede componer el bloque sin esta fecha. */
    issuedAt?: string | null;
}

/** Qué pasó con el DINERO del bono (`WelcomeBonusStatusEnum` del type-kit). */
export type WelcomeBonusStatus = 'PENDING' | 'ACCREDITED' | 'FAILED' | 'UNVERIFIED';

/** Lo que la ficha pinta del asiento del bono. Subconjunto de `WelcomeBonusResponse`. */
export interface WelcomeBonusEntry {
    /** Monto EN CENTAVOS, o `null` cuando el asiento no lo trae. Formatear a pesos es del front. */
    amountCents: number | null;
    status: WelcomeBonusStatus;
    requestedAt: string;
    resolvedAt: string | null;
    /** Por qué el processor rechazó el bono. Solo tiene texto con `status: FAILED`. */
    failReason: string | null;
}

/**
 * Bono de bienvenida del 360. Los dos `status` responden preguntas distintas: el del bloque dice si
 * la LECTURA salió, y `bonus.status` si el dinero se movió. Confundirlos pinta un fallido como pagado.
 */
export interface Customer360WelcomeBonus {
    bonus: WelcomeBonusEntry | null;
    status: BlockStatus;
}

/**
 * Bloques del Cliente 360. `customer`, `wallet`, `card` y `welcomeBonus` traen dato real; compras y
 * prospectos siguen vacíos porque el silo del wizard no expone un publisher que se los sirva.
 */
export interface Customer360 {
    customer: RetailCustomerSummary;
    wallet?: Customer360Wallet;
    card?: Customer360Card;
    welcomeBonus?: Customer360WelcomeBonus;
    purchases?: Customer360Purchase[];
    prospects?: Customer360Prospect[];
}

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

/**
 * `publicListCustomers` — GET /customers — la cartera de la tienda del vendedor, paginada.
 *
 * 🔴 La AUSENCIA de `nextToken` es la única señal de última página. `level` y `kyc` viajan como
 * FilterExpression de DynamoDB, que se aplica DESPUÉS del `limit`: una página puede traer menos
 * ítems que el límite —incluso ninguno— y aun así tener más páginas detrás.
 *
 * La tienda NO es parámetro: sale del scope de RBAC. Mandar `?storeId=` no sirve, el silo lo ignora
 * a propósito (candado anti-IDOR). Requiere `retail.customer.list`.
 */
export function listCustomers(filters: CustomerListFilters = {}): Promise<CustomerListPage> {
    const params = new URLSearchParams();
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters.nextToken) params.set('nextToken', filters.nextToken);
    if (filters.level !== undefined) params.set('level', String(filters.level));
    if (filters.kyc !== undefined) params.set('kyc', String(filters.kyc));
    const query = params.toString();
    return call<CustomerListPage>(`/customers${query ? `?${query}` : ''}`);
}

/**
 * Una página de la búsqueda del padrón. El silo devuelve `{ items, count, truncated }`; `count` no
 * se tipa porque `items.length` ya lo dice.
 */
export interface CustomerSearchPage {
    items: RetailCustomerSummary[];
    /**
     * El padrón cortó en alguno de sus dos topes —25 coincidencias devueltas o 2000 clientes
     * recorridos—: hay más de las que viajaron.
     */
    truncated: boolean;
}

/**
 * Normaliza la respuesta de `/customers/search`. Tolera el arreglo pelado además del sobre, para que
 * el front no dependa del orden de despliegue; sin la marca de tope se asume completa.
 */
export function toCustomerSearchPage(raw: unknown): CustomerSearchPage {
    if (Array.isArray(raw)) return { items: raw as RetailCustomerSummary[], truncated: false };
    const envelope = raw as { items?: unknown; truncated?: unknown } | null;
    // Una forma desconocida NO es «no hay nadie»: devolver vacío le diría al operador que el cliente
    // no existe cuando lo que cambió es el contrato. Las dos causas se nombran por separado porque
    // llevan a lugares distintos: `apiFetch` devuelve `null` ante un 204 o un Content-Type no-JSON.
    if (envelope === null || typeof envelope !== 'object') {
        throw report('la respuesta llegó vacía — un 204 o un Content-Type que no es JSON');
    }
    if (!Array.isArray(envelope.items)) {
        throw report('el sobre no trae `items` — el contrato del silo cambió');
    }
    return {
        items: envelope.items as RetailCustomerSummary[],
        truncated: envelope.truncated === true,
    };
}

/** Arma el error y lo manda a Sentry: si el contrato deriva, no basta con que lo vea el operador. */
function report(detail: string): UnexpectedShapeError {
    const error = new UnexpectedShapeError('GET /customers/search', detail);
    Sentry.captureException(error);
    return error;
}

/**
 * `publicSearchCustomers` — GET /customers/search?q=. Teléfono y CURP completos resuelven exacto; el
 * admin además busca por subcadena (dígitos → teléfono, letras → nombre). El vendedor solo exacto y
 * sin CURP, RFC, domicilio ni notas en la respuesta. Requiere `retail.customer.search`.
 */
export function searchCustomersPage(q: string): Promise<CustomerSearchPage> {
    return call<unknown>(`/customers/search?q=${encodeURIComponent(q)}`).then(toCustomerSearchPage);
}

/** La misma búsqueda para quien no necesita saber si el padrón cortó en su tope. */
export function searchCustomers(q: string): Promise<RetailCustomerSummary[]> {
    return searchCustomersPage(q).then((page) => page.items);
}

/**
 * `publicGetCustomer` — GET /customers/{id}. Requiere `retail.customer.read` con scope TENANT, que
 * el rol del vendedor no tiene: para el portal es un 403. La ficha se lee por `getCustomer360`.
 */
export function getCustomer(id: string): Promise<RetailCustomerSummary> {
    return call<RetailCustomerSummary>(`/customers/${encodeURIComponent(id)}`);
}

/**
 * `publicGetCustomerSummary360` — GET /customers/{id}/summary360. Requiere
 * `retail.customer.summary.read`, sin scope. Al vendedor le llega sin `wallet` ni datos sensibles.
 */
export function getCustomer360(id: string): Promise<Customer360> {
    return call<Customer360>(`/customers/${encodeURIComponent(id)}/summary360`);
}

/* ================================================================================
   DOCUMENTOS DE ONBOARDING — `GET /customers/{id}/documents`.
   ================================================================================ */

/**
 * Veredicto de un bloque que el silo compone desde otro dueño (`BlockStatus`). Los cinco llevan a
 * acciones distintas del operador: aplastarlos a «vacío» le diría que el bloque no existe.
 */
export type BlockStatus = 'OK' | 'DEGRADED' | 'ABSENT' | 'UNAVAILABLE' | 'NO_SOURCE';

/** Qué es el archivo, deducido de cómo lo nombra el silo de identidad al guardarlo. */
export type OnboardingDocumentKind =
    | 'ID_FRONT'
    | 'ID_BACK'
    | 'PROOF_OF_ADDRESS'
    | 'SELFIE'
    | 'OTHER';

/** Un archivo del expediente de identidad. Estos documentos NO tienen estado de firma. */
export interface OnboardingDocument {
    /** Nombre crudo del silo (`MX_NATIONAL_ID_FRONT`, `LIVENESS_SELFIE`, …). */
    name: string;
    kind: OnboardingDocumentKind;
    /** Enlace firmado que VENCE. `null` = el archivo existe y la firma falló, no que no exista. */
    url: string | null;
    /** Peso del objeto en S3, ya redondeado a KB por identity. Todavía no lo compone este silo. */
    sizeKb?: number;
    /** `LastModified` del objeto en S3: cuándo se subió el archivo. NO es una fecha de firma. */
    lastModified?: string;
}

/**
 * Veredicto del ENLACE al PDF del documento legal. `ABSENT` = ese tipo no tiene documento vigente
 * publicado; `UNAVAILABLE` = el documento existe y su lectura falló, y se puede reintentar.
 */
export type LegalDocumentLinkStatus = 'OK' | 'ABSENT' | 'UNAVAILABLE' | 'NO_SOURCE';

/**
 * Asiento de aceptación de un documento legal. El silo manda además la IP y la sesión con las que
 * se consintió: son evidencia PII y esta pantalla NO las pide ni las pinta, así que no se tipan.
 */
export interface LegalAcceptance {
    type: string;
    version: string | null;
    acceptedAt: string | null;
    /** Enlace firmado al PDF del documento aceptado. Ausente en el silo todavía desplegado. */
    documentUrl?: string | null;
    /** Por qué no hay enlace. `ABSENT` no es lo mismo que `UNAVAILABLE`: ver `LegalDocumentLinkStatus`. */
    documentLinkStatus?: LegalDocumentLinkStatus;
}

/** Respuesta de `GET /customers/{id}/documents`: el expediente y sus dos veredictos. */
export interface CustomerDocumentsResponse {
    retailCustomerId: string;
    onboarding: OnboardingDocument[];
    privacyNotice: LegalAcceptance[];
    blocks: {
        onboarding: BlockStatus;
        privacyNotice: BlockStatus;
    };
}

/**
 * `publicGetCustomerDocuments` — GET /customers/{id}/documents. Requiere
 * `retail.customer.documents.read` con scope TENANT. Solo ONBOARDING: los contratos del crédito
 * los sirve `loan-credit-business` por su propio endpoint.
 */
export function getCustomerDocuments(id: string): Promise<CustomerDocumentsResponse> {
    return call<CustomerDocumentsResponse>(`/customers/${encodeURIComponent(id)}/documents`);
}

/** `publicUpdateCustomer` — PUT /customers/{id}. Requiere `retail.customer.update`. */
export function updateCustomer(
    id: string,
    input: UpdateCustomerInput,
): Promise<RetailCustomerSummary> {
    return call<RetailCustomerSummary>(`/customers/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
    });
}

/** `publicListKycPending` — GET /customers/kyc-pending. Requiere `retail.customer.kyc.pending.list`. */
export function listKycPending(): Promise<RetailCustomerSummary[]> {
    return call<RetailCustomerSummary[]>(`/customers/kyc-pending`);
}

/** `publicReviewCustomerKyc` — POST /customers/{id}/kyc-review. Requiere `retail.customer.kyc.review`. */
export function reviewCustomerKyc(
    id: string,
    result: KycReviewResult,
    justification: string,
): Promise<RetailCustomerSummary> {
    return call<RetailCustomerSummary>(`/customers/${encodeURIComponent(id)}/kyc-review`, {
        method: 'POST',
        body: JSON.stringify({ result, justification }),
    });
}
